/**
 * AdBanner — provider-aware inline banner slot for the web app.
 *
 * Renders the active web ad provider's banner (Ezoic / AdSense) when configured,
 * otherwise a tasteful "house" banner (premium upsell) so the slot is testable
 * today. Premium users never see ads. Mobile has its own AdMob banners.
 */
import EzoicSlot from './EzoicSlot';
import AdSenseSlot from './AdSenseSlot';
import { activeBannerProvider } from '../lib/webAdProvider';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles } from 'lucide-react';

function HouseBanner({ onPremium }) {
  return (
    <div
      className="rounded-xl border border-dashed border-border/70 bg-gradient-to-br from-accent/8 to-transparent px-4 py-3 flex items-center justify-between gap-3"
      data-testid="ad-banner-house"
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold truncate">Enjoying Fantasy World Star?</p>
        <p className="text-xs text-muted-foreground">Go ad-free forever — a one-time upgrade, no gameplay advantage.</p>
      </div>
      {onPremium && (
        <button
          onClick={onPremium}
          className="shrink-0 text-xs font-semibold rounded-full px-3 py-1.5 bg-[hsl(var(--accent))] text-white hover:opacity-90 transition-opacity"
          data-testid="ad-banner-premium-cta"
        >
          Remove ads
        </button>
      )}
    </div>
  );
}

export default function AdBanner({ placeholderId, className = '', onPremium }) {
  const { user } = useAuth();
  if (user?.premium) return null; // premium = no ads, ever

  const provider = activeBannerProvider();
  if (provider === 'none') return null;

  const bannerId = Number(placeholderId || process.env.REACT_APP_EZOIC_BANNER_ID || '0');

  return (
    <div className={`w-full ${className}`} data-testid="ad-banner">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70 mb-1 flex items-center gap-1">
        <Sparkles className="h-2.5 w-2.5" /> Advertisement
      </div>
      {provider === 'ezoic' ? (
        <EzoicSlot placeholderId={bannerId} minHeight={90} testId="ezoic-slot-banner" />
      ) : provider === 'adsense' ? (
        <AdSenseSlot />
      ) : (
        <HouseBanner onPremium={onPremium} />
      )}
    </div>
  );
}
